/**
 * Real routing/URL/history (PRD §8 gap table) — the prototype's `navStack` was in-memory
 * only and reset on reload. This mirrors its stack semantics (push, back, reset-to-Home on
 * flow completion) while keeping the browser's URL and back button in sync, using the
 * History API directly rather than a third-party router.
 *
 * A pushed navigation's own history-state entry carries a `depth` counter so `back()` and
 * the physical/browser back button funnel through the same `popstate` handler; `resetTo()`
 * (personalize save, invite done, install finish — see `flows.md`) pushes a fresh baseline
 * at depth 0. 🔸 Known limitation, documented in implementation-notes.md: a `resetTo` does
 * not erase the entries that preceded it (no browser API does), so one extra physical
 * back-tap after a completed flow can briefly revisit its last step before the app's own
 * guard/redirect logic takes over.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useAuthStatus } from './authStatus'
import { composeGuards, type RouteGuard } from './guards'
import { buildPath, matchPath, HOME_ROUTE, type Route } from './routes'

interface HistoryState {
  readonly depth: number
  /**
   * How many overlay entries sit on top of this route's own entry (D145).
   *
   * An overlay pushes an entry at the **same path and the same `depth`** — it is not a
   * navigation, and `canGoBack` must not move because a drawer opened. This counter is the only
   * thing that tells the two kinds of entry apart on the way back, since `popstate` reports the
   * entry being *landed on* and never the one being left.
   */
  readonly overlays?: number
}

export interface Navigation {
  readonly route: Route
  readonly canGoBack: boolean
  navigate(route: Route): void
  replace(route: Route): void
  back(): void
  resetTo(route: Route): void
}

/** One open overlay that owns a history entry, as registered by `useDismissOnBack`. */
interface OverlayRecord {
  readonly key: string
  readonly dismissible: boolean
}

/**
 * The overlay half of the router (D145). Consumed only by `useDismissOnBack` — a screen never
 * calls this directly, because the hook is what keeps an overlay's own `open` state and the
 * history entry from drifting apart.
 */
export interface OverlayHistory {
  /** Bottom-to-top keys of the overlays that currently own a history entry. */
  readonly openKeys: readonly string[]
  open(key: string, dismissible: boolean): void
  setDismissible(key: string, dismissible: boolean): void
  close(key: string): void
}

const RouterContext = createContext<Navigation | undefined>(undefined)
const OverlayHistoryContext = createContext<OverlayHistory | undefined>(undefined)

function readDepth(): number {
  const state = window.history.state as HistoryState | null
  return typeof state?.depth === 'number' ? state.depth : 0
}

function readOverlays(): number {
  const state = window.history.state as HistoryState | null
  return typeof state?.overlays === 'number' ? state.overlays : 0
}

function writeHistory(mode: 'push' | 'replace', route: Route, depth: number, overlays: number): void {
  const state: HistoryState = { depth, overlays }
  const path = buildPath(route)
  if (mode === 'push') window.history.pushState(state, '', path)
  else window.history.replaceState(state, '', path)
}

export function Router({
  children,
  guards = [],
}: {
  readonly children: ReactNode
  readonly guards?: readonly RouteGuard[]
}): JSX.Element {
  const auth = useAuthStatus()
  const guard = useMemo(() => composeGuards(guards), [guards])

  const [route, setRoute] = useState<Route>(() => {
    const matched = matchPath(window.location.pathname) ?? HOME_ROUTE
    const resolved = guard(matched, auth)
    return resolved === true ? matched : resolved
  })
  /**
   * **Boot adopts the entry's own depth; it does not reset it to 0.**
   *
   * `depth` lives in React state, and React state does not survive the page being torn down —
   * but the history entry's `state` does. An installed PWA that the OS evicts while backgrounded
   * comes back as a *reload* on the same entry: same URL, same session history, no React. Writing
   * 0 here threw away the only surviving record of how deep that entry was, so `canGoBack` went
   * false and `back()` became a no-op — while every screen still rendered its back button, because
   * a screen passes `onBack` unconditionally. That is the "sometimes the back button does nothing
   * after leaving and re-entering the app" report: *sometimes*, because it needs the OS to have
   * actually discarded the page.
   *
   * `readDepth()` returns 0 for an entry with no state of its own (a cold deep link, a fresh
   * standalone launch), which is correct — there is genuinely nothing behind it. `back()` below
   * owns what a back button does in that case.
   */
  const [depth, setDepth] = useState(() => readDepth())
  const [overlays, setOverlays] = useState<readonly OverlayRecord[]>([])

  /**
   * Live mirrors for the `popstate` listener and for `open`/`close`, which run outside React's
   * render cycle and must see the value as of *now* rather than as of the render that installed
   * them. `overlays` in particular is read and written within a single event handler
   * (`commit()` unwinds and navigates in the same tick), which a state variable cannot serve.
   */
  const overlaysRef = useRef(overlays)
  overlaysRef.current = overlays
  const routeRef = useRef(route)
  routeRef.current = route
  const depthRef = useRef(depth)
  depthRef.current = depth
  /** A navigation requested while overlays were open, held until their entries have unwound. */
  const pendingNav = useRef<{
    readonly mode: 'push' | 'replace'
    readonly route: Route
    readonly depth: number
  } | null>(null)
  /**
   * A queued reconcile of the entries the stack no longer needs (D145 follow-up).
   *
   * **A close cannot traverse in the same tick that another overlay opens**, which is exactly what
   * one overlay *replacing* another does: the drawer's Update and Add-to-home-screen rows close
   * the drawer and open a sheet in one batch, so `close()`'s `history.go` and `open()`'s
   * `pushState` are two operations against the same entry with no ordering between them.
   * `history.go` is queued, and **Chrome resolves the delta against the index as of the call**,
   * not as of the task — measured, not assumed — so the traversal landed on the *route* entry, one
   * below the drawer's. `popstate` then read `overlays: 0` against a two-deep stack and dismissed
   * everything, including the sheet that had just opened. The row looked dead, and the only
   * surface behind it is the one that applies a waiting shell (D137), so a resident could never
   * reach the Update button at all.
   *
   * So the shrink is deferred by one microtask and made **relative rather than absolute**: by the
   * time it runs, every close *and* open of the batch has been recorded, and it traverses only the
   * surplus that is genuinely left. A replacement produces a surplus of zero and issues no
   * traversal at all, which is also what makes this immune to the delta-resolution question above.
   */
  const pendingUnwind = useRef(false)

  /**
   * Stamp the boot entry so the first popstate has a state to read, preserving whatever depth
   * the entry already carried across a reload.
   *
   * **`overlays` is normalized to 0 here, and `depth` deliberately is not.** A reload builds a
   * fresh React tree with no overlay open, so an entry claiming otherwise describes a drawer
   * that no longer exists — unlike `depth`, which describes session history the reload did not
   * touch. 🔸 Residual, and it is the same shape as the `resetTo` limitation above: an OS
   * teardown while an overlay was open leaves that overlay's spent entry in the stack, so one
   * back tap after such a relaunch is absorbed before the next one navigates. No browser API
   * removes an entry, and re-opening the overlay is worse — a rename popover would come back
   * with its typed text gone.
   */
  useEffect(() => {
    writeHistory('replace', route, readDepth(), 0)
    // Intentionally boot-only; navigate/back/resetTo own every state change after this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const commitNow = useCallback(
    (mode: 'push' | 'replace', requested: Route, requestedDepth: number): void => {
      const resolved = guard(requested, auth)
      const target = resolved === true ? requested : resolved
      const targetDepth = resolved === true ? requestedDepth : depthRef.current
      // Always 0: a real navigation lands on a route entry, and every overlay that was open has
      // had its entry unwound by `commit()` before this runs.
      writeHistory(mode, target, targetDepth, 0)
      setRoute(target)
      setDepth(targetDepth)
    },
    [auth, guard],
  )

  useEffect(() => {
    function onPopState(): void {
      /**
       * The landing of a `history.go(-n)` issued by `commit()`. It comes first because the
       * entry we arrive on is the route's own — indistinguishable from a plain back — and the
       * navigation that caused the unwind is still owed.
       */
      const pending = pendingNav.current
      if (pending !== null) {
        pendingNav.current = null
        commitNow(pending.mode, pending.route, pending.depth)
        return
      }

      const stack = overlaysRef.current
      const nextOverlays = readOverlays()
      if (nextOverlays < stack.length) {
        const top = stack[stack.length - 1]
        /**
         * A dialog that refuses the backdrop while a write is in flight must refuse the back
         * button too, or a mid-flight delete gets a cancelled-looking UI over a request that is
         * still running. The entry is already spent by the time `popstate` fires, so refusing
         * means pushing a replacement — the only way back to the state we were in.
         */
        if (top !== undefined && !top.dismissible) {
          writeHistory('push', routeRef.current, depthRef.current, stack.length)
          return
        }
        const remaining = stack.slice(0, nextOverlays)
        overlaysRef.current = remaining
        setOverlays(remaining)
        // Overlay entries carry the route's own path, so there is no route to re-resolve.
        return
      }

      const matched = matchPath(window.location.pathname) ?? HOME_ROUTE
      const nextDepth = readDepth()
      const resolved = guard(matched, auth)
      const target = resolved === true ? matched : resolved
      if (resolved !== true) writeHistory('replace', target, nextDepth, 0)
      setRoute(target)
      setDepth(nextDepth)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [auth, guard, commitNow])

  // A session change (sign-out, LRU eviction, revoke) re-runs the guard against whatever
  // route is currently showing, so a screen that required auth stops rendering immediately.
  // Any open overlay goes with it: the screen holding it is being replaced, so its entry is
  // reclaimed here rather than left behind to absorb a later back tap.
  useEffect(() => {
    const resolved = guard(route, auth)
    if (resolved !== true) {
      overlaysRef.current = []
      setOverlays([])
      writeHistory('replace', resolved, depth, 0)
      setRoute(resolved)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth])

  const value = useMemo<Navigation>(() => {
    function commit(mode: 'push' | 'replace', requested: Route, requestedDepth: number): void {
      /**
       * Navigating out from under an open overlay (the drawer's Install-gate and About rows).
       * The overlay's entries are unwound *first* and the navigation deferred to the landing,
       * because pushing on top of them would leave a spent entry that a later back tap would
       * absorb — the drawer would appear to reopen, or nothing would happen at all.
       *
       * Clearing the stack synchronously is what stops the double-unwind: the caller's own
       * `setDrawerOpen(false)` renders in this same batch, and `useDismissOnBack` must see its
       * key already gone so it does not issue a second `history.back()` for the same entry.
       */
      // `readOverlays()`, not the stack's own length: those agree except in the window where a
      // close has truncated the stack and its microtask has not traversed yet, and there the
      // entry is still on the history stack and still owed.
      const drop = readOverlays()
      if (drop > 0) {
        pendingNav.current = { mode, route: requested, depth: requestedDepth }
        pendingUnwind.current = false
        overlaysRef.current = []
        setOverlays([])
        window.history.go(-drop)
        return
      }
      commitNow(mode, requested, requestedDepth)
    }

    return {
      route,
      canGoBack: depth > 0,
      navigate: (next) => commit('push', next, depth + 1),
      replace: (next) => commit('replace', next, depth),
      resetTo: (next) => commit('push', next, 0),
      back: () => {
        if (depth > 0) {
          window.history.back()
          return
        }
        /**
         * Depth 0 on a screen that shows a back button — a cold launch straight into an inner
         * route (a deep link, a standalone relaunch with no session history behind it). Home is
         * the honest destination: doing nothing leaves a control that looks like the way out of
         * the screen and is not, which is the same defect `AppBar`'s `title-only` bar exists to
         * avoid. `replace`, not `push`, so the dead-end entry is not kept behind Home.
         */
        if (route.id !== HOME_ROUTE.id) commit('replace', HOME_ROUTE, 0)
      },
    }
  }, [route, depth, commitNow])

  const overlayHistory = useMemo<OverlayHistory>(
    () => ({
      openKeys: overlays.map((entry) => entry.key),
      open(key, dismissible) {
        const next = [...overlaysRef.current.filter((entry) => entry.key !== key), { key, dismissible }]
        overlaysRef.current = next
        setOverlays(next)
        /**
         * **Only when the stack has grown past what history already holds.** An overlay that
         * replaced one closed in this same batch inherits the entry that close was about to give
         * up, rather than pushing a second one on top of it — the resident performed one gesture
         * and owes one back tap, and pushing here would leave the spent entry underneath to
         * absorb it.
         *
         * The push itself is unchanged: same path, same depth, because an overlay is not a
         * navigation and `canGoBack` must not move because one opened.
         */
        if (readOverlays() < next.length) {
          writeHistory('push', routeRef.current, depthRef.current, next.length)
        }
      },
      setDismissible(key, dismissible) {
        const current = overlaysRef.current
        const found = current.find((entry) => entry.key === key)
        if (found === undefined || found.dismissible === dismissible) return
        const next = current.map((entry) => (entry.key === key ? { key, dismissible } : entry))
        overlaysRef.current = next
        setOverlays(next)
      },
      /**
       * A backdrop tap or a Cancel button. The entry is still spent through `history.go` — a
       * programmatic close must never leave one behind for a later back tap to absorb — but the
       * traversal is deferred to `pendingUnwind`'s microtask, so an overlay opening in the same
       * batch can claim the entry instead of the browser discarding it.
       *
       * The stack is truncated **here rather than on the landing `popstate`**, because that is
       * what the `open()` beside it reads to compose the replacement. Closing a lower overlay
       * still closes everything above it; those hooks learn it from `openKeys` exactly as they
       * did when `popstate` was the one truncating.
       */
      close(key) {
        const index = overlaysRef.current.findIndex((entry) => entry.key === key)
        if (index === -1) return
        const next = overlaysRef.current.slice(0, index)
        overlaysRef.current = next
        setOverlays(next)
        if (pendingUnwind.current) return
        pendingUnwind.current = true
        queueMicrotask(() => {
          // Cleared by `commit()`, which unwinds every overlay entry itself and owes a navigation
          // on the landing — a second traversal here would overshoot it.
          if (!pendingUnwind.current) return
          pendingUnwind.current = false
          const surplus = readOverlays() - overlaysRef.current.length
          if (surplus > 0) window.history.go(-surplus)
        })
      },
    }),
    [overlays],
  )

  return (
    <RouterContext.Provider value={value}>
      <OverlayHistoryContext.Provider value={overlayHistory}>{children}</OverlayHistoryContext.Provider>
    </RouterContext.Provider>
  )
}

export function useNavigation(): Navigation {
  const ctx = useContext(RouterContext)
  if (ctx === undefined) throw new Error('useNavigation() called outside <Router>')
  return ctx
}

export function useOverlayHistory(): OverlayHistory {
  const ctx = useContext(OverlayHistoryContext)
  if (ctx === undefined) throw new Error('useDismissOnBack() called outside <Router>')
  return ctx
}

export function useRoute(): Route {
  return useNavigation().route
}
